import React, { useCallback, useEffect, useRef } from "react";
import { basename } from "path";
import { observer } from "mobx-react-lite";
import { container } from "@/utils/mobx";
import { BrowsableEntry, FileBrowserStore, GitFileStatus } from "./store";
import { SandboxStore } from "@/app_store/agent";
import { t } from "@/i18n";
import "./s.file_browser.css";


interface TreeNodeRowProps {
    entry: BrowsableEntry;
    depth: number;
    isSelected: boolean;
    isExpanded: boolean;
    isLoading: boolean;
    clickMode: "single" | "double";
    gitStatus?: GitFileStatus | null;
    actions?: React.ReactNode;
    onToggleExpand: () => void;
    onSelect: () => void;
    onOpen: () => void;
    onContextMenu: (x: number, y: number) => void;
}

const TreeNodeRow = ({ entry, depth, isSelected, isExpanded, isLoading, clickMode, gitStatus, actions, onToggleExpand, onSelect, onOpen, onContextMenu }: TreeNodeRowProps) => (
    <div
        className={`file_browser__entry${isSelected ? " file_browser__entry--selected" : ""}`}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
        onClick={() => {
            if (entry.is_dir) onToggleExpand();
            onSelect();
            if (!entry.is_dir && clickMode === "single") onOpen();
        }}
        onDoubleClick={() => {
            if (!entry.is_dir && clickMode === "double") onOpen();
        }}
        onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onContextMenu(e.clientX, e.clientY);
        }}
    >
        {entry.is_dir ? (
            <span className={`file_browser__tree_arrow${isExpanded ? " file_browser__tree_arrow--open" : ""}`} />
        ) : (
            <span className="file_browser__tree_arrow_spacer" />
        )}
        {!entry.is_dir && <span className="file_browser__entry_icon file_browser__entry_icon--file" />}
        <span className={`file_browser__entry_name${gitStatus === "added" ? " file_browser__entry_name--git-added" : gitStatus === "modified" ? " file_browser__entry_name--git-modified" : ""}`}>{basename(entry.path)}</span>
        {actions && <div className="file_browser__entry_actions" onClick={(e) => e.stopPropagation()}>{actions}</div>}
        {entry.is_dir && isLoading && <span className="file_browser__spinner" />}
    </div>
);

interface FlatEntryRowProps {
    entry: BrowsableEntry;
    isSelected: boolean;
    clickMode: "single" | "double";
    gitStatus?: GitFileStatus | null;
    actions?: React.ReactNode;
    onSelect: () => void;
    onNavigate: () => void;
    onContextMenu: (x: number, y: number) => void;
}

const FlatEntryRow = ({ entry, isSelected, clickMode, gitStatus, actions, onSelect, onNavigate, onContextMenu }: FlatEntryRowProps) => (
    <div
        className={`file_browser__entry${isSelected ? " file_browser__entry--selected" : ""}`}
        onClick={() => {
            onSelect();
            if (entry.is_dir && clickMode === "single") onNavigate();
        }}
        onDoubleClick={() => { if (entry.is_dir && clickMode === "double") onNavigate(); }}
        onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onContextMenu(e.clientX, e.clientY);
        }}
    >
        {!entry.is_dir && <span className="file_browser__entry_icon file_browser__entry_icon--file" />}
        <span className={`file_browser__entry_name${gitStatus === "added" ? " file_browser__entry_name--git-added" : gitStatus === "modified" ? " file_browser__entry_name--git-modified" : ""}`}>{basename(entry.path)}</span>
        {actions && <div className="file_browser__entry_actions" onClick={(e) => e.stopPropagation()}>{actions}</div>}
        {entry.is_dir && (
            <button
                className="file_browser__enter_btn"
                onClick={(e) => { e.stopPropagation(); onNavigate(); }}
                title={t("file_open_dir")}
            >
                <span className="file_browser__enter_icon" />
            </button>
        )}
    </div>
);


const FolderTree = observer(({ path, depth, store, clickMode, onOpen, onContextMenu }: {
    path: string;
    depth: number;
    store: FileBrowserStore;
    clickMode: "single" | "double";
    onOpen?: (path: string, isDir: boolean) => void;
    onContextMenu?: (path: string, isDir: boolean, x: number, y: number) => void;
}) => {
    const entries = store.getSortedDirectoryFiles(path);

    return (
        <>
            {entries.map(entry => (
                <React.Fragment key={entry.path}>
                    <TreeNodeRow
                        entry={entry}
                        depth={depth}
                        isSelected={store.selectedPath === entry.path}
                        isExpanded={store.expandedPaths.has(entry.path)}
                        isLoading={store.isDirectoryLoading(entry.path)}
                        clickMode={clickMode}
                        gitStatus={store.gitStatusMap.get(entry.path) ?? null}
                        actions={store.renderActions(entry.path, entry.is_dir)}
                        onToggleExpand={() => store.toggleExpand(entry.path)}
                        onSelect={() => store.select(entry.path, entry.is_dir)}
                        onOpen={() => onOpen?.(entry.path, entry.is_dir)}
                        onContextMenu={(x, y) => onContextMenu?.(entry.path, entry.is_dir, x, y)}
                    />
                    {entry.is_dir && store.expandedPaths.has(entry.path) && !store.isDirectoryLoading(entry.path) && (
                        <FolderTree
                            path={entry.path}
                            depth={depth + 1}
                            store={store}
                            clickMode={clickMode}
                            onOpen={onOpen}
                            onContextMenu={onContextMenu}
                        />
                    )}
                </React.Fragment>
            ))}
        </>
    );
});


export const FileBrowser = container((props: {
    store: FileBrowserStore;
    mode?: "flat" | "tree";
    clickMode?: "single" | "double";
    rootPath: string;
    sandbox?: SandboxStore | null;
    activePath?: string | null;
    enableAutoScroll?: boolean;
    onSelect?: (path: string | null, isDir: boolean) => void;
    onOpen?: (path: string, isDir: boolean) => void;
    onContextMenu?: (path: string, isDir: boolean, x: number, y: number) => void;
    collapseRef?: React.MutableRefObject<((maxDepth: number) => void) | null>;
}) => {
    const store = props.store;
    const clickMode = props.clickMode ?? "double";
    const enableAutoScroll = props.enableAutoScroll ?? false;
    const selectedPath = store.selectedPath; // reactive access so FileBrowser re-renders on selection change
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!enableAutoScroll) return;
        const el = listRef.current?.querySelector(".file_browser__entry--selected") as HTMLElement | null;
        el?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, [selectedPath, enableAutoScroll]);

    if (props.collapseRef) {
        props.collapseRef.current = (maxDepth: number) => store.collapseToDepth(maxDepth);
    }

    const handleBack = useCallback(() => store.back(), []);
    const handleForward = useCallback(() => store.forward(), []);
    const handleUp = useCallback(() => store.up(), []);

    const handleBackgroundContextMenu = props.onContextMenu
        ? (e: React.MouseEvent) => { e.preventDefault(); props.onContextMenu!("/", true, e.clientX, e.clientY); }
        : undefined;

    if (store.mode === "tree") {
        const rootEntries = store.getSortedDirectoryFiles(store.rootPath);
        return (
            <div
                className={`file_browser file_browser--tree${clickMode === "single" ? " file_browser--single-click" : ""}`}
                onContextMenu={handleBackgroundContextMenu}
            >
                <div className="file_browser__list file_browser__list--tree" ref={listRef}>
                    {store.isDirectoryLoading(store.rootPath) && rootEntries.length === 0 ? (
                        <div className="file_browser__loading_row" style={{ paddingLeft: "8px" }}>
                            <span className="file_browser__spinner" />
                        </div>
                    ) : rootEntries.length === 0 ? (
                        <div className="file_browser__empty">{t("file_empty")}</div>
                    ) : (
                        <FolderTree
                            path={store.rootPath}
                            depth={0}
                            store={store}
                            clickMode={clickMode}
                            onOpen={props.onOpen}
                            onContextMenu={props.onContextMenu}
                        />
                    )}
                </div>
            </div>
        );
    }

    const flatEntries = store.getSortedDirectoryFiles(store.currentPath);

    return (
        <div
            className={`file_browser${clickMode === "single" ? " file_browser--single-click" : ""}`}
            onContextMenu={handleBackgroundContextMenu}
        >
            <div className="file_browser__toolbar">
                <button
                    className="file_browser__nav_btn"
                    onClick={handleBack}
                    disabled={!store.canGoBack}
                    title="Back"
                >
                    <span className="file_browser__nav_icon file_browser__nav_icon--back" />
                </button>
                <button
                    className="file_browser__nav_btn"
                    onClick={handleForward}
                    disabled={!store.canGoForward}
                    title="Forward"
                >
                    <span className="file_browser__nav_icon file_browser__nav_icon--forward" />
                </button>
                <button
                    className="file_browser__nav_btn"
                    onClick={handleUp}
                    disabled={!store.canGoUp}
                    title="Go up"
                >
                    <span className="file_browser__nav_icon file_browser__nav_icon--up" />
                </button>
                <span className="file_browser__current_name" title={store.currentPath}>{store.currentName}</span>
            </div>
            <div className="file_browser__list">
                {flatEntries.length === 0 ? (
                    store.isDirectoryLoading(store.currentPath) ? (
                        <div className="file_browser__loading_row">
                            <span className="file_browser__spinner" />
                        </div>
                    ) : (
                        <div className="file_browser__empty">{t("file_empty_dir")}</div>
                    )
                ) : flatEntries.map(entry => (
                    <FlatEntryRow
                        key={entry.path}
                        entry={entry}
                        isSelected={store.selectedPath === entry.path}
                        clickMode={clickMode}
                        gitStatus={store.gitStatusMap.get(entry.path) ?? null}
                        actions={store.renderActions(entry.path, entry.is_dir)}
                        onSelect={() => store.select(entry.path, entry.is_dir)}
                        onNavigate={() => store.navigate(entry.path)}
                        onContextMenu={(x, y) => props.onContextMenu?.(entry.path, entry.is_dir, x, y)}
                    />
                ))}
            </div>
        </div>
    );
}, FileBrowserStore);
